import { CheckIcon, ChevronDownIcon, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useState, type FC } from "react";
import { Input } from "../ui/input";
import Text from "../Text";
import { useTranslation } from "react-i18next";
import { setActionTriggerNeedCore } from "@/store/restrictedFeatureState";

const OPTIONS_COUNTRIES = [
  {
    label: "option_country_france",
    value: "FR",
  },
  {
    label: "option_country_england",
    value: "GB",
  },
  {
    label: "option_country_belgium",
    value: "BE",
  },
];

type Props = {
  onChange: (value: string[]) => void;
  isRestricted: boolean;
  value: string[];
};

const SearchCountries: FC<Props> = ({ onChange, value, isRestricted }) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="secondaryFilled"
          role="combobox"
          aria-expanded={open}
          className="w-[100%] justify-between bg-[white]"
          style={{
            border: "1px solid #B3B9C4",
          }}
          onClick={() => {
            if (isRestricted) {
              setActionTriggerNeedCore(true);
            }
          }}
        >
          <Text className="truncate">
            {value.length > 0
              ? value
                  .map((v) =>
                    t(
                      OPTIONS_COUNTRIES.find((c) => c.value === v)?.label || "",
                    ),
                  )
                  .join(", ")
              : t("select_countries")}
          </Text>

          {isRestricted ? (
            <Lock
              width={24}
              height={24}
              color="#535862"
              className="ml-2 h-4 w-4 shrink-0"
            />
          ) : (
            <ChevronDownIcon
              width={24}
              height={24}
              color="#758195"
              className="ml-2 h-4 w-4 shrink-0"
              style={{
                transition: "transform 0.3s",
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          )}
        </Button>
      </PopoverTrigger>
      {!isRestricted && (
        <PopoverContent className="w-[355px] p-[12px]">
          <Input
            placeholder={t("research_country")}
            onChange={(e) => setSearch(e.target.value)}
            value={search}
          />
          <div className="flex flex-col gap-[8px] my-[8px]">
            {OPTIONS_COUNTRIES.map((country, index) => (
              <button
                key={`value-${country.value}-${index}`}
                onClick={() => {
                  if (value.includes(country.value)) {
                    onChange(value.filter((v) => v !== country.value));
                  } else {
                    onChange([...value, country.value]);
                  }
                }}
                className="flex items-center gap-[8px] px-[12px] py-[8px] hover:bg-[#F1F2F4]"
              >
                {value.includes(country.value) && (
                  <CheckIcon height={16} width={16} />
                )}
                <Text fontSize={16} fontWeight={400}>
                  {t(country.label)}
                </Text>
              </button>
            ))}
          </div>
        </PopoverContent>
      )}
    </Popover>
  );
};

export default SearchCountries;
